import { IconButton, Button } from '@perch-labs/shrike';
import { useCallback, useEffect, useState } from 'react';
import { UserForm } from '~/components/dialogs/edit-information';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import { Inputs } from '~/components/inputs';
import { Container } from '~/components/layout';
import { ReviewBlock } from '~/components/review-block';
import { useForm } from '~/hooks';
import { UpdateUserInput } from '../../review-and-view-terms.wrapper';
import { reviewAndViewTermsCopy } from '../review-and-view-terms.copy';
import { StyledCrate, StyledTwoColumnGrid } from '../review-and-view-terms.style';

export interface EditUserComponentProps {
  userId: number;
  initialFormDetails: UserForm;
  closeEditForm: boolean;
  handleUserSubmission: (data: UpdateUserInput) => void;
  resetEditUserError: () => void;
  error?: Error | null;
  loading?: boolean;
}

export const EditUserComponent = (props: EditUserComponentProps) => {
  const {
    userId,
    initialFormDetails,
    closeEditForm,
    handleUserSubmission,
    resetEditUserError,
    error,
    loading,
  } = props;
  const { userDetailsEditableTab } = reviewAndViewTermsCopy;
  const [shouldEdit, setShouldEdit] = useState(closeEditForm);
  const icon = shouldEdit ? 'UIClose' : 'Pencil';
  const title = shouldEdit ? userDetailsEditableTab.editTitle : userDetailsEditableTab.title;

  const [form, updateForm, isValid, resetForm] = useForm({
    firstName: initialFormDetails.firstName,
    lastName: initialFormDetails.lastName,
    phone: initialFormDetails.phone,
  });

  useEffect(() => {
    setShouldEdit(false);
  }, [initialFormDetails]);

  const handleUserEdit = useCallback(() => {
    setShouldEdit(!shouldEdit);
    resetEditUserError();
    resetForm();
  }, [shouldEdit, resetEditUserError, resetForm]);

  useEffect(() => {
    if (closeEditForm) {
      handleUserEdit();
    }
  }, [closeEditForm]);

  const submit = () => {
    handleUserSubmission({
      id: userId,
      input: { ...form, email: initialFormDetails.email },
    });
  };

  return (
    <StyledCrate>
      <ReviewBlock>
        <StyledTwoColumnGrid>
          <ReviewBlock.Item label={title} />
          <IconButton
            aria-label="edit-user-details"
            icon={icon}
            type="button"
            mode="primary"
            onClick={handleUserEdit}
          />
        </StyledTwoColumnGrid>
        {shouldEdit ? (
          <Container addInputMargin>
            <Form isValid={isValid} onSubmit={submit}>
              <Inputs.FirstName
                disabled={loading}
                marginBottom={16}
                onChange={updateForm}
                value={form.firstName}
              />
              <Inputs.LastName
                disabled={loading}
                marginBottom={16}
                onChange={updateForm}
                value={form.lastName}
              />
              <Inputs.Phone
                label="Phone"
                name="phone"
                disabled={loading}
                marginBottom={16}
                onChange={updateForm}
                value={form.phone!}
              />
              <ErrorMessage error={error?.message} />
              <Button
                disabled={!isValid}
                type="submit"
                mode="default"
                fullWidth
                onSubmit={submit}
                loading={loading}
              >
                Save changes
              </Button>
            </Form>
          </Container>
        ) : (
          <>
            <ReviewBlock.Item label={userDetailsEditableTab.name} notranslate>
              {`${initialFormDetails.firstName} ${initialFormDetails.lastName}`}
            </ReviewBlock.Item>
            <ReviewBlock.Item label={userDetailsEditableTab.email} notranslate>
              {initialFormDetails.email}
            </ReviewBlock.Item>
            <ReviewBlock.Item label={userDetailsEditableTab.phone} notranslate>
              {initialFormDetails.phone}
            </ReviewBlock.Item>
          </>
        )}
      </ReviewBlock>
    </StyledCrate>
  );
};
